import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { apiClient } from '../../../config/apiConfig';
import AmplificationGroupFlow from './AutomatedTasks/AmplificationGroupFlow';
import agencyOverviewService from '../../../services/agencyOverviewService';

const CampaignDashboardContent = () => {
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [campaignContextId, setCampaignContextId] = useState('');
  const [error, setError] = useState(null);
  const [operationsSummary, setOperationsSummary] = useState(null);
  const [operationsContextId, setOperationsContextId] = useState('');
  const [operationsLoading, setOperationsLoading] = useState(true);
  const [operationsError, setOperationsError] = useState('');
  const campaignRequestRef = useRef({ version: 0, controller: null });
  const operationsRequestRef = useRef({ version: 0, controller: null });

  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    campaignRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const version = campaignRequestRef.current.version + 1;
    campaignRequestRef.current = { version, controller };
    try {
      setError(null);
      const response = await apiClient.get(`/campaigns/${campaignId}`, { signal: controller.signal });
      if (controller.signal.aborted || campaignRequestRef.current.version !== version) return;
      setCampaign(response.data);
      setCampaignContextId(campaignId);
    } catch (err) {
      if (controller.signal.aborted || campaignRequestRef.current.version !== version || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Error loading campaign:', err);
      if ([401, 403, 404].includes(err.response?.status)) setCampaign(null);
      setCampaignContextId(campaignId);
      setError('Failed to load campaign data. Please try again.');
      toast.error('Failed to load campaign data');
    } finally {
      if (!controller.signal.aborted && campaignRequestRef.current.version === version) setLoading(false);
    }
  }, [campaignId]);

  const loadOperationsSummary = useCallback(async () => {
    if (!campaignId) return;
    operationsRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const version = operationsRequestRef.current.version + 1;
    operationsRequestRef.current = { version, controller };
    try {
      setOperationsLoading(true);
      setOperationsError('');
      setOperationsContextId(campaignId);
      const nextSummary = await agencyOverviewService.getCampaignSummary(campaignId, { signal: controller.signal });
      if (controller.signal.aborted || operationsRequestRef.current.version !== version) return;
      setOperationsSummary(nextSummary);
    } catch (err) {
      if (controller.signal.aborted || operationsRequestRef.current.version !== version || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      console.error('Error loading Campaign operations summary:', err);
      setOperationsError('Campaign readiness could not be loaded.');
    } finally {
      if (!controller.signal.aborted && operationsRequestRef.current.version === version) setOperationsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    setCampaign(null);
    setCampaignContextId('');
    setOperationsSummary(null);
    setOperationsContextId('');
    loadCampaign();
    loadOperationsSummary();
    return () => {
      campaignRequestRef.current.version += 1;
      campaignRequestRef.current.controller?.abort();
      operationsRequestRef.current.version += 1;
      operationsRequestRef.current.controller?.abort();
    };
  }, [campaignId, loadCampaign, loadOperationsSummary]);

  if (loading || campaignContextId !== campaignId) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center" role="status" aria-label="Loading campaign">
        <IconLoader2 size={32} className="animate-spin text-accent" aria-hidden="true" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="text-center" role="alert">
          <p className="text-error mb-4">{error || 'Campaign not found'}</p>
          <button
            type="button"
            onClick={() => { setLoading(true); loadCampaign(); }}
            className="min-h-11 px-4 py-2 bg-accent text-accent-foreground hover:bg-accent-hover"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const refresh = async () => Promise.all([loadCampaign(), loadOperationsSummary()]);
  const operationsAreCurrent = operationsContextId === campaignId;
  return <>
    {error && <div className="m-4 flex flex-wrap items-center justify-between gap-3 border border-error p-3 text-sm text-error" role="alert">
      <p>Campaign refresh failed. Previously loaded details are still shown.</p>
      <button type="button" onClick={loadCampaign} className="min-h-11 border border-error px-3">Retry campaign details</button>
    </div>}
    <AmplificationGroupFlow campaignData={campaign} onRefresh={refresh} isExternal={campaign.campaignType === 'EXTERNAL'} operationsSummary={operationsAreCurrent ? operationsSummary : null} operationsSummaryLoading={!operationsAreCurrent || operationsLoading} operationsSummaryError={operationsAreCurrent ? operationsError : ''} onOperationsRefresh={loadOperationsSummary} />
  </>;
};

const CampaignDashboard = () => {
  return <CampaignDashboardContent />;
};

export default CampaignDashboard;
